// Decides when a network problem has really stopped Live transcription.
//
// Signals (Jack's spec, 2026-10-22):
// - browser `offline` event        -> lost immediately
// - fatal Speech SDK error         -> lost immediately
// - Speech SDK `disconnected`      -> warning now; lost if not reconnected within
//                                     DISCONNECT_GRACE_MS (a reconnect cancels it)
// - backend heartbeat (every 60 s) -> one failure is not proof of an outage. It
//                                     counts while the SDK is disconnected, or
//                                     after HEARTBEAT_FAILURE_LIMIT in a row. A
//                                     session the server says is closed is lost.
// The 180 s backend stale-session sweep is billing recovery, not this delay.

export const DISCONNECT_GRACE_MS = 5000;
export const HEARTBEAT_FAILURE_LIMIT = 2;

export const LOST_REASONS = Object.freeze({
  OFFLINE: 'offline',
  DISCONNECTED: 'disconnected',
  FATAL: 'fatal_error',
  HEARTBEAT: 'heartbeat',
  SESSION_CLOSED: 'session_closed',
});

/** The backend's answer for a session that is no longer OPEN (settled or swept). */
export function isClosedSessionError(error) {
  return /live session is not open/i.test(error?.message || '');
}

export function createLiveConnectionMonitor({
  onWarning, onRecovered, onLost, graceMs = DISCONNECT_GRACE_MS,
  target = typeof window !== 'undefined' ? window : null,
} = {}) {
  let lost = false;
  let disposed = false;
  let graceTimer = null;
  let sdkDisconnected = false;
  let heartbeatFailures = 0;

  const clearGrace = () => {
    if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
  };
  const lose = reason => {
    if (lost || disposed) return;
    lost = true;
    clearGrace();
    onLost?.(reason);
  };

  const onOffline = () => lose(LOST_REASONS.OFFLINE);
  target?.addEventListener?.('offline', onOffline);

  return {
    sdkDisconnected() {
      if (lost || disposed || sdkDisconnected) return;
      sdkDisconnected = true;
      onWarning?.();
      graceTimer = setTimeout(() => lose(LOST_REASONS.DISCONNECTED), graceMs);
    },
    sdkConnected() {
      if (lost || disposed || !sdkDisconnected) return;
      sdkDisconnected = false;
      clearGrace();
      onRecovered?.();
    },
    browserOffline() { lose(LOST_REASONS.OFFLINE); },
    fatalError() { lose(LOST_REASONS.FATAL); },
    heartbeatSucceeded() { heartbeatFailures = 0; },
    heartbeatFailed(error) {
      if (lost || disposed) return;
      if (isClosedSessionError(error)) { lose(LOST_REASONS.SESSION_CLOSED); return; }
      heartbeatFailures += 1;
      if (sdkDisconnected || heartbeatFailures >= HEARTBEAT_FAILURE_LIMIT) lose(LOST_REASONS.HEARTBEAT);
    },
    get isLost() { return lost; },
    dispose() {
      disposed = true;
      clearGrace();
      target?.removeEventListener?.('offline', onOffline);
    },
  };
}
